import { useRouter } from 'next/router';
import React, { useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import VideoPreviewModal from './videoPreviewModal';

import { NextImage } from '@components/atoms/image';
import Carousel, { CarouselItemPosition } from '@components/share/carousel';
import { getNamespaces } from '@helpers/i18nUtils';
import { useBreakpointV3 } from '@helpers/use';
import { IHighlightVideo } from '@interfaces/university';

type VideoPreviewSectionProps = {
  videos: IHighlightVideo[];
  autoPlaySpeed?: number;
  degreeName: string;
  majorName: string;
};

const VideoPreviewSection = ({ videos, autoPlaySpeed = 5000, degreeName, majorName }: VideoPreviewSectionProps) => {
  const theme = useTheme();
  const router = useRouter();
  const degreeIdName = (router.query.degreeIdName as string) || '';
  const { t } = useTranslation(getNamespaces({ namespaces: [degreeIdName] }));
  const [selectedVideo, setSelectedVideo] = useState<IHighlightVideo | null>(null);

  const handleOpenVideoPreviewModal = (video: IHighlightVideo) => {
    setSelectedVideo(video);
  };

  const handleCloseVideoPreviewModal = () => {
    setSelectedVideo(null);
  };

  const { isMobile, isTablet } = useBreakpointV3();

  const minItemsForCarousel = isMobile || isTablet ? 2 : 3;

  const renderVideoItem = (video: IHighlightVideo, index: number, position: CarouselItemPosition) => (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <NextImage
        src={video.thumbnailUrl}
        alt="video thumbnail"
        layout="fill"
        objectFit="cover"
        objectPosition="center"
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <NextImage src="/static/icons/video-preview-play-icon.svg" alt="play button" width="60px" height="60px" />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ paddingBottom: { lg: '80px', md: '48px', xs: '48px' } }}>
      <Typography
        variant={isMobile ? 'h3' : 'h1'}
        sx={{ textAlign: 'center', color: theme.palette.textOnSurface.primary }}
      >
        {t('degree_landing_page.video_preview_section.title')}
      </Typography>
      <Typography
        variant={isMobile ? 'h4' : 'h3'}
        sx={{ textAlign: 'center', color: theme.palette.textOnSurface.secondary }}
      >
        {t('degree_landing_page.degree_highlight_video.subtitle', {
          degreeName,
          majorName,
        })}
      </Typography>

      <Box>
        <Carousel
          items={videos}
          animationMode="scale"
          renderItem={renderVideoItem}
          keyExtractor={(video) => video.videoUrl}
          onItemClick={handleOpenVideoPreviewModal}
          minItemsForCarousel={minItemsForCarousel}
          showDots
          showArrows={!isMobile}
          trackDimensions={{ height: { lg: '250px', md: '252px', xs: '154px' } }}
          scaleItemDimensions={{
            center: { width: { md: '444px', xs: '273px' }, height: { lg: '250px', md: '252px', xs: '154px' } },
            side: { width: { md: '322px', xs: '180px' }, height: { md: '182px', xs: '102px' } },
            gap: { lg: '24px', md: '24px', xs: '16px' },
          }}
          dotStyles={{
            width: '40px',
            height: '8px',
            gap: '16px',
            borderRadius: 'unset',
          }}
        />
      </Box>

      {selectedVideo && (
        <VideoPreviewModal
          open={!!selectedVideo}
          onClose={handleCloseVideoPreviewModal}
          videoUrl={selectedVideo.videoUrl}
          thumbnailUrl={selectedVideo.thumbnailUrl}
        />
      )}
    </Box>
  );
};

export default VideoPreviewSection;
